import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import {
    fetchDownloadDicomImageBySOP,
    fetchDownloadDicomSeries,
    useGetSession,
} from '~/backend/connector-api/connector-components';
import { SessionPresentation } from '~/backend/connector-api/connector-schemas';
import { ImportResult } from '~/domain/services/dicomProcessor';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import {
    sessionDataAtom,
    sessionInitStatusAtom,
    SessionInitStatus,
} from '~/state/atoms/sessionAtoms';
import { handleError } from '~/utils/errors';
import { withRetryOn202 } from '~/backend/connector-api/withRetryOn202';

/**
 * Initializes a connector session: fetches session metadata,
 * stores it in sessionDataAtom, and auto-downloads the
 * context image and optional structure set.
 *
 * Download and environment initialization are split into two
 * effects because the 3D viewport environment may not be ready
 * when the DICOM files finish processing (the Viewport component
 * is a sibling that initializes the engine asynchronously).
 *
 * Status is tracked in sessionInitStatusAtom for consumers.
 */
export default function useSessionInitialization(
    sessionId: string
): SessionInitStatus {
    const [sessionData, setSessionData] = useRecoilState(sessionDataAtom);
    const [initStatus, setInitStatus] = useRecoilState(sessionInitStatusAtom);
    const { processImportedFiles } = useProcessDicomFiles();
    const { environment, initializeEnvironment, setEnvironmentPrimaryImage } =
        useViewportEnvironment();
    const hasStartedDownload = useRef(false);
    const hasStartedInit = useRef(false);

    // Holds processed DICOM data until the viewport environment
    // is ready to receive it.
    const [pendingImport, setPendingImport] = useState<ImportResult | null>(
        null
    );
    const contextSeriesUidRef = useRef('');

    // Reset refs and local state when sessionId changes so a
    // new session can initialize cleanly without stale guards.
    useEffect(() => {
        hasStartedDownload.current = false;
        hasStartedInit.current = false;
        contextSeriesUidRef.current = '';
        setPendingImport(null);
    }, [sessionId]);

    const {
        data: sessionDataFromApi,
        error: sessionError,
        isLoading: isSessionLoading,
    } = useGetSession<SessionPresentation>({
        pathParams: { sessionID: sessionId },
    });

    // Store fetched session data into the atom
    useEffect(() => {
        if (sessionDataFromApi) {
            setSessionData(sessionDataFromApi);
        }
    }, [sessionDataFromApi, setSessionData]);

    // Effect 1: Download context image + optional structure set.
    // Stores processed items in state; does NOT touch the viewport
    // environment (it may not be ready yet).
    useEffect(() => {
        if (!sessionData || hasStartedDownload.current) {
            return;
        }

        const contextImage = sessionData.payload?.context_image;
        if (
            !contextImage?.series_instance_uid ||
            !contextImage?.study_instance_uid
        ) {
            setInitStatus({ isLoading: false, hasStarted: true, error: null });
            return;
        }

        hasStartedDownload.current = true;
        contextSeriesUidRef.current = contextImage.series_instance_uid;

        const fetchAndProcess = async () => {
            setInitStatus({ isLoading: true, hasStarted: true, error: null });

            try {
                const dicomBlob = await withRetryOn202(
                    async () =>
                        await fetchDownloadDicomSeries({
                            pathParams: {
                                studyId: contextImage.study_instance_uid,
                                seriesId: contextImage.series_instance_uid,
                            },
                            queryParams: { series: true },
                        })
                );

                const files = [
                    new File(
                        [dicomBlob],
                        `${contextImage.series_instance_uid}.zip`
                    ),
                ];

                const structureSet = sessionData.payload?.context_structure_set;
                if (
                    structureSet?.sop_instance_uid &&
                    sessionData.payload?.patient?.id
                ) {
                    const structureSetBlob = await withRetryOn202(
                        async () =>
                            await fetchDownloadDicomImageBySOP({
                                pathParams: {
                                    patientID: sessionData.payload!.patient.id,
                                    modality: 'RTSTRUCT',
                                    sopInstanceUID:
                                        structureSet.sop_instance_uid,
                                },
                            })
                    );
                    files.push(
                        new File(
                            [structureSetBlob],
                            `${structureSet.sop_instance_uid}.dcm`
                        )
                    );
                }

                const processedItems = await processImportedFiles(files);
                if (processedItems) {
                    setPendingImport(processedItems);
                } else {
                    setInitStatus({
                        isLoading: false,
                        hasStarted: true,
                        error: null,
                    });
                }
            } catch (fetchError) {
                const error = fetchError as Error;
                handleError(
                    fetchError,
                    "Couldn't process DICOM files. Try again."
                );
                setInitStatus({ isLoading: false, hasStarted: true, error });
            }
        };

        fetchAndProcess();
    }, [sessionData, setInitStatus, processImportedFiles]);

    // Effect 2: Initialize the viewport environment once both
    // the processed DICOM data AND the 3D engine are available.
    // When environmentState changes (engine init), the functions
    // from useViewportEnvironment update, triggering this effect.
    useEffect(() => {
        if (!pendingImport || !environment || hasStartedInit.current) {
            return;
        }
        hasStartedInit.current = true;

        const init = async () => {
            await initializeEnvironment(pendingImport);

            // Force-select the context image when multiple images
            // were loaded (initializeEnvironment skips multi-
            // candidate scenarios).
            if (pendingImport.images.length > 1) {
                const contextImg = pendingImport.images.find(
                    (img) => img.seriesUid === contextSeriesUidRef.current
                );
                const patientId = pendingImport.patients[0]?.PatientID ?? '';
                if (contextImg) {
                    setEnvironmentPrimaryImage([contextImg], patientId);
                }
            }

            setPendingImport(null);
            setInitStatus({ isLoading: false, hasStarted: true, error: null });
        };

        init();
    }, [
        pendingImport,
        environment,
        initializeEnvironment,
        setEnvironmentPrimaryImage,
        setInitStatus,
    ]);

    // Combine API fetch loading with DICOM download loading
    // so the first render already shows loading=true
    return useMemo<SessionInitStatus>(() => {
        const error =
            initStatus.error ||
            (sessionError instanceof Error
                ? sessionError
                : sessionError
                  ? new Error(String(sessionError))
                  : null);
        return {
            isLoading: isSessionLoading || initStatus.isLoading,
            hasStarted: initStatus.hasStarted,
            error,
        };
    }, [isSessionLoading, sessionError, initStatus]);
}
